import axios from 'axios'
import { getResponseData, normalizeQueryError } from 'src/queries/network.utils'
import { getRequestConfig, RequestConfig } from 'src/queries/requestConfig'

import {
  AccountCollectionFlowCompletionResponse,
  AccountCollectionFlowResponse,
  BillPaymentDetailsResponse,
  BillPaymentError,
  BillPaymentHistoryCriteria,
  BillPaymentHistoryPage,
  BillPaymentInvoiceAttachmentResponse,
  BillPaymentResponse,
  BillPaymentStatusResponse,
  CancelBillPaymentAttempt,
  CompleteFundingAccountVerificationRequest,
  ConfirmBillPaymentAttempt,
  CreatePaymentConnectionRequest,
  CreatePaymentConnectionResponse,
  FundingAccountVerificationResponse,
  PaymentConnectionResponse,
  PaymentFundingAccountDetails,
  ResendBillPaymentAuthorizationAttempt,
  RestartBillPaymentAuthorizationAttempt,
  ScheduleBillPaymentAttempt,
  UploadBillPaymentInvoiceAttachmentAttempt,
} from './payments.types'
import { extractKybValidationError } from './paymentsKybError'

const CONNECTION_BASE = '/payments/connection'
const ACCOUNT_COLLECTION_FLOW_PATH = '/payments/account-collection-flow'
const FUNDING_ACCOUNT_PATH = '/payments/funding-account'
const FUNDING_ACCOUNT_VERIFICATION_PATH =
  '/payments/funding-account/verification'
const PAYMENT_INTENTS_PATH = '/payments/payment-intents'
const INVOICE_ATTACHMENTS_PATH = '/payments/invoice-attachments'
const IMF_FIXDATE =
  /^(Sun|Mon|Tue|Wed|Thu|Fri|Sat), (0[1-9]|[12]\d|3[01]) (Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) (\d{4}) ([01]\d|2[0-3]):([0-5]\d):([0-5]\d) GMT$/

const parseImfFixdate = (value: string): number | undefined => {
  if (!IMF_FIXDATE.test(value)) {
    return undefined
  }
  const parsed = Date.parse(value)
  if (!Number.isFinite(parsed) || new Date(parsed).toUTCString() !== value) {
    return undefined
  }
  return parsed
}

const billPaymentError = (caught: unknown): BillPaymentError => {
  const normalized = normalizeQueryError(caught) as BillPaymentError
  const original = normalized.originalError
  const retryAfter = axios.isAxiosError(original)
    ? original.response?.headers?.['retry-after']
    : undefined
  const seconds = Number(retryAfter)
  if (Number.isFinite(seconds)) {
    if (seconds > 0) {
      normalized.retryAfterSeconds = Math.ceil(seconds)
    }
    return normalized
  }
  if (typeof retryAfter === 'string') {
    const retryAfterDate = parseImfFixdate(retryAfter)
    if (retryAfterDate !== undefined) {
      const remainingSeconds = (retryAfterDate - Date.now()) / 1000
      if (remainingSeconds > 0) {
        normalized.retryAfterSeconds = Math.ceil(remainingSeconds)
      }
    }
  }
  return normalized
}

const mutationConfig = (
  idempotencyKey: string,
  revision?: number,
): ReturnType<typeof getRequestConfig> => {
  const config = getRequestConfig(RequestConfig.Default)
  return {
    ...config,
    headers: {
      ...config.headers,
      'Idempotency-Key': idempotencyKey,
      ...(revision === undefined ? {} : { 'If-Match': `"${revision}"` }),
    },
  }
}

const multipartMutationConfig = (
  idempotencyKey: string,
): ReturnType<typeof getRequestConfig> => {
  const config = getRequestConfig(RequestConfig.Form)
  return {
    ...config,
    headers: {
      ...config.headers,
      'Idempotency-Key': idempotencyKey,
    },
  }
}

const requireId = (value: string, operation: string, name: string): void => {
  if (!value.trim()) {
    throw new Error(`${operation} requires a ${name}`)
  }
}

const requirePositiveRevision = (revision: number, operation: string): void => {
  if (!Number.isInteger(revision) || revision <= 0) {
    throw new Error(`${operation} requires a positive revision`)
  }
}

const requireIdempotencyKey = (key: string, operation: string): void => {
  if (!key.trim() || key.length > 255) {
    throw new Error(`${operation} requires a valid idempotencyKey`)
  }
}

const PAYMENT_STATES = [
  'DRAFT',
  'AWAITING_AUTHORIZATION',
  'SCHEDULED',
  'CANCELLED',
] as const
const AUTHORIZATION_STATES = [
  'PENDING',
  'CONSUMED',
  'LOCKED',
  'EXPIRED',
  'CANCELLED',
] as const
const DELIVERY_STATES = ['PENDING', 'SENT', 'FAILED'] as const
const ATTACHMENT_ASSOCIATION_STATES = ['UNATTACHED', 'ATTACHED'] as const
const ATTACHMENT_SCAN_STATES = ['PENDING', 'CLEAN', 'INFECTED'] as const
const ATTACHMENT_CONTENT_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
] as const
const HISTORY_DISPLAY_STATUSES = [
  'ACTION_REQUIRED',
  'PROCESSING',
  'COMPLETED',
  'FAILED',
  'RETURNED',
  'CANCELLED',
] as const
const HISTORY_AVAILABLE_ACTIONS = ['RESUME_AUTHORIZATION', 'CANCEL'] as const
const PAYMENT_EXECUTION_STATUSES = [
  'PROCESSING',
  'COMPLETED',
  'FAILED',
  'RETURNED',
] as const
const PAYMENT_EXECUTION_FAILURE_REASONS = [
  'PRECONDITION_FAILED',
  'PROVIDER_REJECTED',
] as const
const PAYMENT_EXECUTION_FAILED_LEGS = ['FUNDING', 'PAYOUT'] as const
const HISTORY_ACTIVITY_TYPES = [
  'CONFIRMED',
  'AUTHORIZATION_REQUESTED',
  'SCHEDULED',
  'PROCESSING',
  'REMITTANCE_EMAIL_SENT',
  'COMPLETED',
  'FAILED',
  'RETURNED',
  'CANCELLED',
] as const

type WireRecord = Record<string, unknown>

const isRecord = (value: unknown): value is WireRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0
const isNullableString = (value: unknown): value is string | null =>
  value === null || typeof value === 'string'
const isPositiveInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0
const isNonNegativeInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0
const ISO_INSTANT_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,9})?Z$/
const normalizeDateParts = (
  year: number,
  month: number,
  day: number,
): string | undefined => {
  if (year < 1000 || year > 9999 || month < 1 || month > 12) {
    return undefined
  }
  const date = new Date(Date.UTC(year, month - 1, day))
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() + 1 !== month ||
    date.getUTCDate() !== day
  ) {
    return undefined
  }
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}
const normalizeTimestamp = (value: unknown): string | undefined => {
  if (typeof value === 'number') {
    // Bill Payment instants are Unix epoch milliseconds. Requiring the
    // trillion-millisecond range rejects an accidental epoch-seconds payload.
    if (!Number.isSafeInteger(value) || value < 1_000_000_000_000) {
      return undefined
    }
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString()
  }
  if (!isNonEmptyString(value)) return undefined
  const match = ISO_INSTANT_PATTERN.exec(value)
  if (match === null) return undefined
  const [
    ,
    yearValue,
    monthValue,
    dayValue,
    hourValue,
    minuteValue,
    secondValue,
  ] = match
  const year = Number(yearValue)
  const month = Number(monthValue)
  const day = Number(dayValue)
  const hour = Number(hourValue)
  const minute = Number(minuteValue)
  const second = Number(secondValue)
  if (
    normalizeDateParts(year, month, day) === undefined ||
    hour > 23 ||
    minute > 59 ||
    second > 59
  ) {
    return undefined
  }
  return Number.isNaN(Date.parse(value)) ? undefined : value
}
const normalizeNullableTimestamp = (
  value: unknown,
): string | null | undefined =>
  value === null ? null : normalizeTimestamp(value)
const normalizeDate = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
    if (match === null) return undefined
    const normalized = normalizeDateParts(
      Number(match[1]),
      Number(match[2]),
      Number(match[3]),
    )
    return normalized === value ? value : undefined
  }
  if (
    !Array.isArray(value) ||
    value.length !== 3 ||
    !value.every(Number.isInteger)
  ) {
    return undefined
  }
  const [year, month, day] = value as number[]
  return normalizeDateParts(year, month, day)
}
const normalizeNullableDate = (value: unknown): string | null | undefined =>
  value === null ? null : normalizeDate(value)
const isOneOf = <T extends string>(
  value: unknown,
  allowed: readonly T[],
): value is T =>
  typeof value === 'string' && (allowed as readonly string[]).includes(value)
// Optional + nullable field predicates: an absent field (undefined) is tolerated
// so a pre-projection backend passes, while a present field is still validated.
const isOptionalNullableOneOf = <T extends string>(
  value: unknown,
  allowed: readonly T[],
): boolean => value === undefined || value === null || isOneOf(value, allowed)
const isOptionalNullableString = (value: unknown): boolean =>
  value === undefined || isNullableString(value)

const invalidResponse = (
  errorCode: string,
  error: string,
  paymentId?: string,
): BillPaymentError => ({ error, errorCode, status: 200, paymentId })

const isResponseValidationError = (error: unknown): error is BillPaymentError =>
  isRecord(error) &&
  typeof error.errorCode === 'string' &&
  error.errorCode.endsWith('_RESPONSE_INVALID')

const validAttachmentSummary = (value: unknown): boolean =>
  isRecord(value) &&
  isNonEmptyString(value.id) &&
  isOneOf(value.associationStatus, ATTACHMENT_ASSOCIATION_STATES) &&
  isOneOf(value.scanStatus, ATTACHMENT_SCAN_STATES)

const validateHistoryActor = (value: unknown): boolean =>
  isRecord(value) &&
  isNonEmptyString(value.id) &&
  isNullableString(value.displayName) &&
  isNullableString(value.email)

const validateHistoryRow = (value: unknown): boolean => {
  const createdAt = isRecord(value)
    ? normalizeTimestamp(value.createdAt)
    : undefined
  const dueDate = isRecord(value)
    ? normalizeNullableDate(value.invoiceDueDate)
    : undefined
  return (
    isRecord(value) &&
    isNonEmptyString(value.id) &&
    isPositiveInteger(value.revision) &&
    createdAt !== undefined &&
    isRecord(value.vendor) &&
    isNonEmptyString(value.vendor.id) &&
    isNonEmptyString(value.vendor.name) &&
    dueDate !== undefined &&
    isPositiveInteger(value.amountCents) &&
    value.currency === 'USD' &&
    isNonEmptyString(value.invoiceNumber) &&
    (value.invoiceAttachment === null ||
      (isRecord(value.invoiceAttachment) &&
        isNonEmptyString(value.invoiceAttachment.id) &&
        value.invoiceAttachment.associationStatus === 'ATTACHED' &&
        value.invoiceAttachment.scanStatus === 'CLEAN')) &&
    validateHistoryActor(value.sender) &&
    isOneOf(value.displayStatus, HISTORY_DISPLAY_STATUSES) &&
    Array.isArray(value.availableActions) &&
    value.availableActions.every((action) =>
      isOneOf(action, HISTORY_AVAILABLE_ACTIONS),
    )
  )
}

const validateHistoryPage = (
  value: unknown,
  expected: Pick<BillPaymentHistoryCriteria, 'page' | 'count'>,
): BillPaymentHistoryPage => {
  if (
    !isRecord(value) ||
    !Array.isArray(value.payments) ||
    !value.payments.every(validateHistoryRow) ||
    value.page !== expected.page ||
    value.count !== expected.count ||
    !isNonNegativeInteger(value.page) ||
    !isPositiveInteger(value.count) ||
    !isNonNegativeInteger(value.totalCount) ||
    value.payments.length > value.count
  ) {
    throw invalidResponse(
      'PAYMENT_HISTORY_RESPONSE_INVALID',
      'getBillPaymentHistory returned an invalid history response',
    )
  }
  return value as unknown as BillPaymentHistoryPage
}

const validateExecutionResponse = (value: unknown): boolean =>
  value === null ||
  (isRecord(value) &&
    isOneOf(value.status, PAYMENT_EXECUTION_STATUSES) &&
    normalizeTimestamp(value.statusUpdatedAt) !== undefined &&
    // Enriched failure-detail fields (EX-40566). Optional/nullable so a backend
    // that has not shipped the projection yet still validates; when present they
    // must be well-typed. The whole execution object passes through verbatim, so
    // these fields reach the cache untouched.
    isOptionalNullableOneOf(
      value.failureReason,
      PAYMENT_EXECUTION_FAILURE_REASONS,
    ) &&
    isOptionalNullableString(value.returnCode) &&
    isOptionalNullableOneOf(value.failedLeg, PAYMENT_EXECUTION_FAILED_LEGS))

const validateBankLabel = (value: unknown): boolean =>
  isRecord(value) &&
  isNonEmptyString(value.label) &&
  (value.lastFour === null ||
    (typeof value.lastFour === 'string' && /^\d{4}$/.test(value.lastFour)))

const validateHistoryActivity = (value: unknown): boolean =>
  isRecord(value) &&
  isNonEmptyString(value.id) &&
  isOneOf(value.type, HISTORY_ACTIVITY_TYPES) &&
  isOneOf(value.displayStatus, HISTORY_DISPLAY_STATUSES) &&
  (value.actor === null || validateHistoryActor(value.actor)) &&
  normalizeTimestamp(value.createdAt) !== undefined

const validateHistoryDetail = (value: unknown): boolean => {
  const invoiceDate = isRecord(value)
    ? normalizeNullableDate(value.invoiceDate)
    : undefined
  const paymentDate = isRecord(value)
    ? normalizeNullableDate(value.paymentDate)
    : undefined
  return (
    isRecord(value) &&
    validateHistoryRow(value.payment) &&
    invoiceDate !== undefined &&
    validateBankLabel(value.paymentMethod) &&
    (value.vendorBankAccount === null ||
      validateBankLabel(value.vendorBankAccount)) &&
    (value.payerBankAccount === null ||
      validateBankLabel(value.payerBankAccount)) &&
    paymentDate !== undefined &&
    isNullableString(value.remittanceEmail) &&
    validateExecutionResponse(value.execution) &&
    Array.isArray(value.activities) &&
    value.activities.every(validateHistoryActivity) &&
    isNullableString(value.memo)
  )
}

const validatePaymentResponse = (
  value: unknown,
  errorCode: string,
  operation: string,
  expected?: { id?: string; revision?: number; state?: string },
): BillPaymentResponse => {
  const paymentId =
    isRecord(value) && isNonEmptyString(value.id) ? value.id : undefined
  const invoiceDate = isRecord(value)
    ? normalizeNullableDate(value.invoiceDate)
    : undefined
  const invoiceDueDate = isRecord(value)
    ? normalizeNullableDate(value.invoiceDueDate)
    : undefined
  const eligibleAt = isRecord(value)
    ? normalizeTimestamp(value.eligibleAt)
    : undefined
  const createdAt = isRecord(value)
    ? normalizeTimestamp(value.createdAt)
    : undefined
  const updatedAt = isRecord(value)
    ? normalizeTimestamp(value.updatedAt)
    : undefined
  const authorizedAt = isRecord(value)
    ? normalizeNullableTimestamp(value.authorizedAt)
    : undefined
  const scheduledAt = isRecord(value)
    ? normalizeNullableTimestamp(value.scheduledAt)
    : undefined
  const cancelledAt = isRecord(value)
    ? normalizeNullableTimestamp(value.cancelledAt)
    : undefined
  if (expected?.id !== undefined && paymentId !== expected.id) {
    throw invalidResponse(
      errorCode,
      `${operation} returned the wrong payment id`,
      paymentId,
    )
  }
  if (
    expected?.revision !== undefined &&
    (!isRecord(value) || value.revision !== expected.revision)
  ) {
    throw invalidResponse(
      errorCode,
      `${operation} returned the wrong payment revision`,
      paymentId,
    )
  }
  if (
    expected?.state !== undefined &&
    (!isRecord(value) || value.state !== expected.state)
  ) {
    throw invalidResponse(
      errorCode,
      `${operation} did not return ${expected.state}`,
      paymentId,
    )
  }
  if (
    !isRecord(value) ||
    !isNonEmptyString(value.id) ||
    !isNonEmptyString(value.vendorId) ||
    !isNonEmptyString(value.vendorPaymentMethodId) ||
    !isNonEmptyString(value.vendorName) ||
    !isNullableString(value.vendorRemittanceEmailHint) ||
    !isPositiveInteger(value.revision) ||
    !isOneOf(value.state, PAYMENT_STATES) ||
    !isPositiveInteger(value.amountCents) ||
    value.currency !== 'USD' ||
    !isNonEmptyString(value.invoiceNumber) ||
    invoiceDate === undefined ||
    invoiceDueDate === undefined ||
    !isNullableString(value.memo) ||
    !(
      value.invoiceAttachment === null ||
      validAttachmentSummary(value.invoiceAttachment)
    ) ||
    eligibleAt === undefined ||
    !isNonEmptyString(value.acknowledgementCopyVersion) ||
    createdAt === undefined ||
    updatedAt === undefined ||
    authorizedAt === undefined ||
    scheduledAt === undefined ||
    cancelledAt === undefined
  ) {
    throw invalidResponse(
      errorCode,
      `${operation} returned an invalid payment response`,
      paymentId,
    )
  }

  if (
    (value.state === 'AWAITING_AUTHORIZATION' &&
      (authorizedAt !== null ||
        scheduledAt !== null ||
        cancelledAt !== null)) ||
    (value.state === 'SCHEDULED' &&
      (authorizedAt === null ||
        scheduledAt === null ||
        cancelledAt !== null)) ||
    (value.state === 'CANCELLED' && cancelledAt === null)
  ) {
    throw invalidResponse(
      errorCode,
      `${operation} returned timestamps inconsistent with payment state`,
      value.id,
    )
  }
  return {
    ...value,
    invoiceDate,
    invoiceDueDate,
    eligibleAt,
    createdAt,
    updatedAt,
    authorizedAt,
    scheduledAt,
    cancelledAt,
  } as unknown as BillPaymentResponse
}

const validateAuthorizationResponse = (
  value: unknown,
  errorCode: string,
  operation: string,
  expectedId?: string,
): BillPaymentDetailsResponse['authorization'] => {
  if (value === null) return null
  const expiresAt = isRecord(value)
    ? normalizeTimestamp(value.expiresAt)
    : undefined
  const resendAvailableAt = isRecord(value)
    ? normalizeTimestamp(value.resendAvailableAt)
    : undefined
  if (
    !isRecord(value) ||
    !isNonEmptyString(value.authorizationId) ||
    value.method !== 'EMAIL' ||
    !isOneOf(value.state, AUTHORIZATION_STATES) ||
    !isNullableString(value.destinationHint) ||
    expiresAt === undefined ||
    !isNonNegativeInteger(value.attemptsRemaining) ||
    resendAvailableAt === undefined ||
    !isOneOf(value.deliveryState, DELIVERY_STATES) ||
    (expectedId !== undefined && value.authorizationId !== expectedId)
  ) {
    throw invalidResponse(
      errorCode,
      `${operation} returned an invalid authorization response`,
    )
  }
  return {
    ...value,
    expiresAt,
    resendAvailableAt,
  } as unknown as BillPaymentDetailsResponse['authorization']
}

const validateDetailsResponse = (
  value: unknown,
  errorCode: string,
  operation: string,
  expected?: {
    paymentId?: string
    revision?: number
    paymentState?: string
    authorizationId?: string
    requirePendingAuthorization?: boolean
    requireHistory?: boolean
  },
): BillPaymentDetailsResponse => {
  if (
    !isRecord(value) ||
    !('payment' in value) ||
    !('authorization' in value)
  ) {
    throw invalidResponse(
      errorCode,
      `${operation} returned an invalid envelope`,
    )
  }
  const payment = validatePaymentResponse(value.payment, errorCode, operation, {
    id: expected?.paymentId,
    revision: expected?.revision,
    state: expected?.paymentState,
  })
  const authorization = validateAuthorizationResponse(
    value.authorization,
    errorCode,
    operation,
    expected?.authorizationId,
  )
  const history =
    'history' in value
      ? value.history === null
        ? null
        : validateHistoryDetail(value.history)
          ? value.history
          : undefined
      : undefined
  if (
    ('history' in value && value.history !== null && history === undefined) ||
    (expected?.requireHistory && (history === undefined || history === null))
  ) {
    throw invalidResponse(
      errorCode,
      `${operation} returned an invalid history detail`,
      payment.id,
    )
  }
  if (
    expected?.requirePendingAuthorization &&
    (!authorization || authorization.state !== 'PENDING')
  ) {
    throw invalidResponse(
      errorCode,
      `${operation} did not return a pending authorization`,
      payment.id,
    )
  }
  return {
    payment,
    authorization,
    ...(history === undefined ? {} : { history }),
  } as BillPaymentDetailsResponse
}

const validateAttachmentResponse = (
  value: unknown,
  operation: string,
  expectedId?: string,
): BillPaymentInvoiceAttachmentResponse => {
  const createdAt = isRecord(value)
    ? normalizeTimestamp(value.createdAt)
    : undefined
  const expiresAt = isRecord(value)
    ? normalizeNullableTimestamp(value.expiresAt)
    : undefined
  if (
    !isRecord(value) ||
    !isNonEmptyString(value.id) ||
    (expectedId !== undefined && value.id !== expectedId) ||
    !isNonEmptyString(value.filename) ||
    !isOneOf(value.contentType, ATTACHMENT_CONTENT_TYPES) ||
    !isPositiveInteger(value.sizeBytes) ||
    !isOneOf(value.associationStatus, ATTACHMENT_ASSOCIATION_STATES) ||
    !isOneOf(value.scanStatus, ATTACHMENT_SCAN_STATES) ||
    createdAt === undefined ||
    expiresAt === undefined ||
    !isNullableString(value.downloadUrl)
  ) {
    throw invalidResponse(
      'PAYMENT_ATTACHMENT_RESPONSE_INVALID',
      `${operation} returned an invalid attachment response`,
    )
  }
  return {
    ...value,
    createdAt,
    expiresAt,
  } as unknown as BillPaymentInvoiceAttachmentResponse
}

// ─── Connection & KYB ────────────────────────────────────────────

export async function getPaymentConnection(): Promise<PaymentConnectionResponse> {
  return getResponseData(
    axios.get<PaymentConnectionResponse>(
      CONNECTION_BASE,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

export async function getFundingAccount(): Promise<PaymentFundingAccountDetails> {
  return getResponseData(
    axios.get<PaymentFundingAccountDetails>(
      FUNDING_ACCOUNT_PATH,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

/** `POST /payments/connection` — KYB-ONLY (the split flow). The full KYB PII
 * (SSNs, DOBs, beneficial-owner identities, document bytes) transits to the
 * provider on this single call and is never persisted, logged, or cached; only
 * `legalEntityId` + `kybStatus` come back on the summary (ADR-0005). */
export async function submitLegalEntity(
  request: CreatePaymentConnectionRequest,
): Promise<CreatePaymentConnectionResponse> {
  try {
    const response = await axios.post<CreatePaymentConnectionResponse>(
      CONNECTION_BASE,
      request,
      getRequestConfig(RequestConfig.Default),
    )
    return response.data
  } catch (error) {
    // A KYB validation 422 carries field-level errors the form must surface —
    // throw a typed error the mutation's onError branches on (never the silent
    // pending dead-end). Everything else falls back to the normalized error.
    const validationError = extractKybValidationError(error)
    if (validationError) throw validationError
    throw normalizeQueryError(error)
  }
}

/**
 * Bootstrap the embedded Modern Treasury Account Collection Flow for the
 * org's own funding (pay-from) account — `POST /payments/account-collection-flow`.
 * No routing/account number is sent here at all — the customer types their
 * bank details into MT's own iframe (`MtAccountCollection`), MT creates the
 * ExternalAccount, then the app calls `completeAccountCollectionFlow(flowId)` to
 * link it. The app only mints the flow's short-lived `clientToken` +
 * `publishableKey` with this call.
 */
export async function createAccountCollectionFlow(): Promise<AccountCollectionFlowResponse> {
  return getResponseData(
    axios.post<AccountCollectionFlowResponse>(
      ACCOUNT_COLLECTION_FLOW_PATH,
      undefined,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

/**
 * Complete the embedded flow — `POST /payments/account-collection-flow/{flowId}/
 * complete`. Called from the MT embed's `onSuccess` (MT fires no completion
 * webhook): the backend retrieves the flow from Modern Treasury and links the
 * funding External Account. Returns `{ status: 'COMPLETED', fundingAccount }`
 * (HTTP 200, linked — the connection now carries the funding account) or
 * `{ status: 'PENDING' }` (HTTP 202, MT has not finished — retry). HTTP 404
 * (unknown/foreign flow), 409 (KYB not active or the flow expired/cancelled),
 * and 502 (provider) surface through the normalized error channel.
 */
export async function completeAccountCollectionFlow(
  flowId: string,
): Promise<AccountCollectionFlowCompletionResponse> {
  // Guard the id in the fn (not just the call site): an empty id otherwise
  // builds `/account-collection-flow//complete` and fires it (see
  // src/queries/AGENTS.md).
  if (!flowId) {
    throw new Error('completeAccountCollectionFlow requires a flowId')
  }
  return getResponseData(
    axios.post<AccountCollectionFlowCompletionResponse>(
      `${ACCOUNT_COLLECTION_FLOW_PATH}/${encodeURIComponent(flowId)}/complete`,
      undefined,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

export async function initiateFundingAccountVerification(): Promise<FundingAccountVerificationResponse> {
  return getResponseData(
    axios.post<FundingAccountVerificationResponse>(
      FUNDING_ACCOUNT_VERIFICATION_PATH,
      undefined,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

export async function completeFundingAccountVerification(
  request: CompleteFundingAccountVerificationRequest,
): Promise<FundingAccountVerificationResponse> {
  const { amounts } = request
  if (
    amounts.length !== 2 ||
    amounts.some((amount) => !Number.isInteger(amount))
  ) {
    throw new Error(
      'completeFundingAccountVerification requires two whole-cent amounts',
    )
  }

  return getResponseData(
    axios.post<FundingAccountVerificationResponse>(
      `${FUNDING_ACCOUNT_VERIFICATION_PATH}/complete`,
      request,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

export async function uploadBillPaymentInvoiceAttachment(
  attempt: UploadBillPaymentInvoiceAttachmentAttempt,
): Promise<BillPaymentInvoiceAttachmentResponse> {
  const { file, idempotencyKey } = attempt
  if (!file) {
    throw new Error('uploadBillPaymentInvoiceAttachment requires a file')
  }
  requireIdempotencyKey(idempotencyKey, 'uploadBillPaymentInvoiceAttachment')

  const formData = new FormData()
  formData.append('file', file)
  try {
    const response = await axios.post<BillPaymentInvoiceAttachmentResponse>(
      INVOICE_ATTACHMENTS_PATH,
      formData,
      multipartMutationConfig(idempotencyKey),
    )
    return validateAttachmentResponse(
      response.data,
      'uploadBillPaymentInvoiceAttachment',
    )
  } catch (error) {
    if (isResponseValidationError(error)) throw error
    throw billPaymentError(error)
  }
}

export async function getBillPaymentInvoiceAttachment(
  attachmentId: string,
): Promise<BillPaymentInvoiceAttachmentResponse> {
  requireId(attachmentId, 'getBillPaymentInvoiceAttachment', 'attachmentId')
  try {
    const response = await axios.get<BillPaymentInvoiceAttachmentResponse>(
      `${INVOICE_ATTACHMENTS_PATH}/${encodeURIComponent(attachmentId)}`,
      getRequestConfig(RequestConfig.Default),
    )
    return validateAttachmentResponse(
      response.data,
      'getBillPaymentInvoiceAttachment',
      attachmentId,
    )
  } catch (error) {
    if (isResponseValidationError(error)) throw error
    throw billPaymentError(error)
  }
}

export async function deleteBillPaymentInvoiceAttachment(
  attachmentId: string,
): Promise<void> {
  requireId(attachmentId, 'deleteBillPaymentInvoiceAttachment', 'attachmentId')
  try {
    const response = await axios.delete<void>(
      `${INVOICE_ATTACHMENTS_PATH}/${encodeURIComponent(attachmentId)}`,
      getRequestConfig(RequestConfig.Default),
    )
    return response.data
  } catch (error) {
    throw billPaymentError(error)
  }
}

export async function confirmBillPayment(
  attempt: ConfirmBillPaymentAttempt,
): Promise<BillPaymentDetailsResponse> {
  const { request, idempotencyKey } = attempt
  requireId(request.vendorId, 'confirmBillPayment', 'vendorId')
  requireIdempotencyKey(idempotencyKey, 'confirmBillPayment')
  try {
    const response = await axios.post<BillPaymentDetailsResponse>(
      `${PAYMENT_INTENTS_PATH}/confirm`,
      request,
      mutationConfig(idempotencyKey),
    )
    return validateDetailsResponse(
      response.data,
      'PAYMENT_CONFIRM_RESPONSE_INVALID',
      'confirmBillPayment',
      {
        paymentState: 'AWAITING_AUTHORIZATION',
        requirePendingAuthorization: true,
      },
    )
  } catch (error) {
    if (isResponseValidationError(error)) throw error
    throw billPaymentError(error)
  }
}

export async function getBillPayment(
  paymentId: string,
): Promise<BillPaymentDetailsResponse> {
  requireId(paymentId, 'getBillPayment', 'paymentId')
  try {
    const response = await axios.get<BillPaymentDetailsResponse>(
      `${PAYMENT_INTENTS_PATH}/${encodeURIComponent(paymentId)}`,
      getRequestConfig(RequestConfig.Default),
    )
    return validateDetailsResponse(
      response.data,
      'PAYMENT_GET_RESPONSE_INVALID',
      'getBillPayment',
      // The detail projection was added after the payment-intent contract.
      // Accept an older backend during a rolling deploy, but validate the
      // projection strictly whenever it is present.
      { paymentId },
    )
  } catch (error) {
    if (isResponseValidationError(error)) throw error
    throw billPaymentError(error)
  }
}

/**
 * The pollable status read (EX-40744) — no provider enrichment on the API side,
 * so a 10s poll here costs nothing. Validated strictly: `displayStatus` is what
 * drives the UI's decision to refetch the detail, so a malformed value must fail
 * rather than reach the cache.
 */
export async function getBillPaymentStatus(
  paymentId: string,
): Promise<BillPaymentStatusResponse> {
  requireId(paymentId, 'getBillPaymentStatus', 'paymentId')
  try {
    const response = await axios.get<BillPaymentStatusResponse>(
      `${PAYMENT_INTENTS_PATH}/${encodeURIComponent(paymentId)}/status`,
      getRequestConfig(RequestConfig.Default),
    )
    const value: unknown = response.data
    if (
      !isRecord(value) ||
      !isOneOf(value.displayStatus, HISTORY_DISPLAY_STATUSES) ||
      !validateExecutionResponse(value.execution ?? null)
    ) {
      throw invalidResponse(
        'PAYMENT_STATUS_RESPONSE_INVALID',
        'getBillPaymentStatus returned an invalid status response',
      )
    }
    return value as unknown as BillPaymentStatusResponse
  } catch (error) {
    if (isResponseValidationError(error)) throw error
    throw billPaymentError(error)
  }
}

export async function getBillPaymentHistory(
  criteria: BillPaymentHistoryCriteria,
): Promise<BillPaymentHistoryPage> {
  if (
    !Number.isInteger(criteria.page) ||
    criteria.page < 0 ||
    !Number.isInteger(criteria.count) ||
    criteria.count <= 0 ||
    normalizeTimestamp(criteria.createdFrom) === undefined ||
    normalizeTimestamp(criteria.createdBefore) === undefined
  ) {
    throw new Error('getBillPaymentHistory requires valid paging and dates')
  }
  const params = new URLSearchParams()
  params.set('page', String(criteria.page))
  params.set('count', String(criteria.count))
  if (criteria.search) params.set('search', criteria.search)
  if (criteria.vendorIds !== undefined) {
    if (criteria.vendorIds.length === 0) params.append('vendorId', '')
    else {
      criteria.vendorIds.forEach((vendorId) =>
        params.append('vendorId', vendorId),
      )
    }
  }
  criteria.statuses.forEach((status) => params.append('status', status))
  params.set('createdFrom', criteria.createdFrom)
  params.set('createdBefore', criteria.createdBefore)
  try {
    const response = await axios.get<BillPaymentHistoryPage>(
      PAYMENT_INTENTS_PATH,
      {
        ...getRequestConfig(RequestConfig.Default),
        params,
      },
    )
    return validateHistoryPage(response.data, criteria)
  } catch (error) {
    if (isResponseValidationError(error)) throw error
    throw billPaymentError(error)
  }
}

export async function restartBillPaymentAuthorization(
  attempt: RestartBillPaymentAuthorizationAttempt,
): Promise<BillPaymentDetailsResponse> {
  const { paymentId, revision, idempotencyKey } = attempt
  requireId(paymentId, 'restartBillPaymentAuthorization', 'paymentId')
  requirePositiveRevision(revision, 'restartBillPaymentAuthorization')
  requireIdempotencyKey(idempotencyKey, 'restartBillPaymentAuthorization')
  try {
    const response = await axios.post<BillPaymentDetailsResponse>(
      `${PAYMENT_INTENTS_PATH}/${encodeURIComponent(paymentId)}/authorizations`,
      undefined,
      mutationConfig(idempotencyKey, revision),
    )
    return validateDetailsResponse(
      response.data,
      'PAYMENT_RESTART_RESPONSE_INVALID',
      'restartBillPaymentAuthorization',
      {
        paymentId,
        revision,
        paymentState: 'AWAITING_AUTHORIZATION',
        requirePendingAuthorization: true,
      },
    )
  } catch (error) {
    if (isResponseValidationError(error)) throw error
    throw billPaymentError(error)
  }
}

export async function resendBillPaymentAuthorization(
  attempt: ResendBillPaymentAuthorizationAttempt,
): Promise<BillPaymentDetailsResponse> {
  const { paymentId, revision, authorizationId, idempotencyKey } = attempt
  requireId(paymentId, 'resendBillPaymentAuthorization', 'paymentId')
  requirePositiveRevision(revision, 'resendBillPaymentAuthorization')
  requireId(
    authorizationId,
    'resendBillPaymentAuthorization',
    'authorizationId',
  )
  requireIdempotencyKey(idempotencyKey, 'resendBillPaymentAuthorization')
  try {
    const response = await axios.post<BillPaymentDetailsResponse>(
      `${PAYMENT_INTENTS_PATH}/${encodeURIComponent(paymentId)}/authorizations/${encodeURIComponent(authorizationId)}/resend`,
      undefined,
      mutationConfig(idempotencyKey),
    )
    return validateDetailsResponse(
      response.data,
      'PAYMENT_RESEND_RESPONSE_INVALID',
      'resendBillPaymentAuthorization',
      {
        paymentId,
        revision,
        paymentState: 'AWAITING_AUTHORIZATION',
        authorizationId,
        requirePendingAuthorization: true,
      },
    )
  } catch (error) {
    if (isResponseValidationError(error)) throw error
    throw billPaymentError(error)
  }
}

export async function scheduleBillPayment(
  attempt: ScheduleBillPaymentAttempt,
): Promise<BillPaymentResponse> {
  const { paymentId, revision, authorizationId, code, idempotencyKey } = attempt
  requireId(paymentId, 'scheduleBillPayment', 'paymentId')
  requirePositiveRevision(revision, 'scheduleBillPayment')
  requireId(authorizationId, 'scheduleBillPayment', 'authorizationId')
  if (!/^\d{6}$/.test(code)) {
    throw new Error('scheduleBillPayment requires a six-digit code')
  }
  requireIdempotencyKey(idempotencyKey, 'scheduleBillPayment')

  try {
    const response = await axios.post<BillPaymentResponse>(
      `${PAYMENT_INTENTS_PATH}/${encodeURIComponent(paymentId)}/schedule`,
      { authorizationId, code },
      mutationConfig(idempotencyKey, revision),
    )

    return validatePaymentResponse(
      response.data,
      'PAYMENT_SCHEDULE_RESPONSE_INVALID',
      'scheduleBillPayment',
      { id: paymentId, revision: revision + 1, state: 'SCHEDULED' },
    )
  } catch (error) {
    if (isResponseValidationError(error)) throw error
    throw billPaymentError(error)
  }
}

export async function cancelBillPayment(
  attempt: CancelBillPaymentAttempt,
): Promise<BillPaymentResponse> {
  const { paymentId, revision, idempotencyKey } = attempt
  requireId(paymentId, 'cancelBillPayment', 'paymentId')
  requirePositiveRevision(revision, 'cancelBillPayment')
  requireIdempotencyKey(idempotencyKey, 'cancelBillPayment')
  try {
    const response = await axios.post<BillPaymentResponse>(
      `${PAYMENT_INTENTS_PATH}/${encodeURIComponent(paymentId)}/cancel`,
      undefined,
      mutationConfig(idempotencyKey, revision),
    )
    return validatePaymentResponse(
      response.data,
      'PAYMENT_CANCEL_RESPONSE_INVALID',
      'cancelBillPayment',
      { id: paymentId, revision: revision + 1, state: 'CANCELLED' },
    )
  } catch (error) {
    if (isResponseValidationError(error)) throw error
    throw billPaymentError(error)
  }
}
